import dayjs from "dayjs";
import LogoBtvDarkDe from '../assets/btv-dark-de.png';
import LogoBtvLightDe from '../assets/btv-light-de.png';
import LogoBtvDarkNl from '../assets/btv-dark-nl.png';
import LogoBtvLightNl from '../assets/btv-light-nl.png';
import LogoNetco from '../assets/netco.png';
import { styled } from '@mui/material/styles';
import { useState, useLayoutEffect, useEffect, useRef, useMemo, useCallback } from 'react';
import { Badge, IconButton, Slider, useMediaQuery } from '@mui/material';
import { isMobile } from "react-device-detect";
import { useLocation, useNavigate } from "react-router-dom";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useKeycloak } from "./Keycloak";
import { useSetNotificationPreferenceMutation } from "../api/graphqlApi";
import { useSelector } from "react-redux";
import { getData } from "../api/dataSlice";

const { DEBUG, KEYCLOAK: { CLIENT_ID }, API } = window.conf

export function useWindowDimensions() {

    const isSm = useMediaQuery(theme => theme.breakpoints.down('sm'))
    const appbarHeight = isMobile ? 48 : isSm ? 56 : 64

    const getScale = () => window.visualViewport?.scale || 1;

    const getWindowDimensions = () => ({
        height: window.innerHeight * getScale(),
        width: window.innerWidth * getScale()
    })

    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions)

    useEffect(() => {
        const updateWindowDimensions = () => setWindowDimensions(getWindowDimensions())

        window.addEventListener('resize', updateWindowDimensions);
        window.visualViewport?.addEventListener('resize', updateWindowDimensions);

        return () => {
            window.removeEventListener('resize', updateWindowDimensions);
            window.visualViewport?.removeEventListener('resize', updateWindowDimensions);
        };
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return {
        appbarHeight,
        contentHeight: windowDimensions.height - appbarHeight,
        windowHeight: windowDimensions.height,
        windowWidth: windowDimensions.width
    }
}

export function isTouchscreen() {
    if (window.PointerEvent && ("maxTouchPoints" in navigator)) { // if Pointer Events are supported, check maxTouchPoints
        if (navigator.maxTouchPoints > 0)
            return true;
    } else {
        if (window.matchMedia && window.matchMedia("(any-pointer:coarse)").matches) { // any-pointer:coarse mostly means touchscreen
            return true;
        } else if (window.TouchEvent || ("ontouchstart" in window)) { // exposed touch events API/event handler
            return true;
        }
    }
    return false;
}

export const isIOS = () => typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) // see material ui docs

export function approachNumber(input, goal) {
    return input?.length ? input.reduce((prev, curr) => (Math.abs(curr-goal) < Math.abs(prev-goal) ? curr : prev)) : null
}

export function getAdjustedIndexAndTimestamp(source, date) {
    
    var timestamp = dayjs.isDayjs(date) ? date : dayjs(date)
    var indexAndTimestamp = {}

    if (source && timestamp) {
        var list = source[0]
        var dictByMinute = source[1]
        var dictByHour = {}
        var indexes = [timestamp.$y, timestamp.$M, timestamp.$D, timestamp.$H] // split timestamp in parts
        
        indexAndTimestamp = {
            index: list?.length-1,
            timestamp: timestamp,
        };

        // iterate through dictionary
        for (let i=0; i<indexes.length; i++) {
            const elem = indexes[i]
            if (dictByMinute?.[elem]) {
                dictByMinute = dictByMinute[elem]
            }
        }

        dictByHour = dictByMinute

        if (dictByMinute?.[timestamp.$m]) {
            dictByMinute = dictByMinute[timestamp.$m]
        }

        var newIndex = dictByMinute?.[timestamp.$m]

        // check if minute of timestamp is an available key of the dictionary, otherwise search for prev/next minute in keys
        if (Number.isFinite(newIndex)) {
            newIndex = dictByMinute?.[timestamp.$m]
            indexAndTimestamp.index = newIndex;
            indexAndTimestamp.timestamp = timestamp;
        } else {
            let minutes = Object.keys(dictByMinute)
            let goalMinute = timestamp.$m
            let closestMinute = approachNumber(minutes, goalMinute)

            newIndex = dictByMinute?.[closestMinute]?.[closestMinute]
            
            // search for prev/next hour in keys
            if (Number.isFinite(newIndex)) {
                indexAndTimestamp.timestamp = dayjs(list?.[newIndex]?.[0]);
                indexAndTimestamp.index = newIndex;
            } else {
                let hours = Object.keys(dictByHour ?? null)
                let goalHour = timestamp.$H
                let closestHour = approachNumber(hours, goalHour)
                // let newMinutes = Object.keys(dictByHour?.[closestHour]) // old
                let newMinutes = dictByHour?.[closestHour] ? Object.keys(dictByHour?.[closestHour]) : null
                let newMinute = approachNumber(newMinutes, goalMinute)
                
                newIndex = dictByHour?.[closestHour]?.[newMinute]?.[newMinute]

                if (Number.isFinite(newIndex)) {
                    indexAndTimestamp.index = newIndex;
                    indexAndTimestamp.timestamp = dayjs(list?.[newIndex]?.[0]);
                } else {
                    console.error("timestamp invalid, index not existing", newIndex)
                }
            }
        }
    }

    return indexAndTimestamp;
}

export const saveFile = (file, name) => {
    const a = document.createElement('a')
    a.download = name
    if ('Blob' in window && file instanceof Blob) {
        a.href = URL.createObjectURL(file)
        a.addEventListener('click', () => setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000))
    } else {
        a.href = file
    }
    a.click()
    a.remove()
}

export const generateTuples = (imageStreams) => {

    if (DEBUG) {
        console.time("[Utilities] generateTuples")
    }
    
    var list = []
    var listIndex = 0
    var dict = {}

    // input is a list of n reverted lists of filenames of cameras
    if (Array.isArray(imageStreams)) {

        // execute at least once
        var runAgain = true

        // last valid element
        var dummys = []

        // as long as an array still has elements
        while (runAgain) { // slower: imageStreams.some(array => array.length)
            runAgain = false

            // get last element of each list
            var firstImages = [] // slower: imageStreams.map(array => array.length ? array[0] : null)

            for (let i = 0; i < imageStreams.length; i++) {
                var array = imageStreams[i]

                if (array?.length) {
                    firstImages[i] = array[array.length-1] // slower: firstImages.push(array[array.length-1])

                    // as long as an list still has elements
                    runAgain = true
                } else {
                    firstImages[i] = null // slower: firstImages.push(null)
                }
            }

            // finished when list has no more elements
            if (!runAgain) {
                break
            }

            // get minimum of the elements from first elements without null
            var min = findMin(firstImages.filter(elem => elem))

            // build element for result
            var elemKey = null
            var elemTuple = []
            var date

            for (let i = 0; i < firstImages.length; i++) {

                // check if one of the elements equals minimum
                // if (firstImages[i].datum === min) { // old
                if (firstImages[i]?.datum === min) {

                    // remove last element of a list when its first element was minimum
                    imageStreams[i].pop()

                    // use first valid element in tuple as key for each element
                    date = databaseDateToObject(firstImages[i].datum)
                    // elemKey = dayjs(firstImages[i], "YY-MM-DD_HH-mm.jpg") // dayjs
                    elemKey = new Date(date.year, date.month, date.day, date.hour, date.minute) // native date
                    
                    // add equal elements to tuple
                    elemTuple[i] = firstImages[i] // slower: elemTuple.push(firstImages[i])

                    // set elements to dummys
                    dummys[i] = firstImages[i] // slower: dummys.push(firstImages[i])
                } else {
                    if (dummys[i]) {
                        elemTuple[i] = dummys[i] // slower: elemTuple.push(dummys[i])
                    } else {
                        // TODO: display test image?
                        elemTuple[i] = null // slower: elemTuple.push(null)
                    }
                }
            }

            // add array of key and tuple to result
            list[listIndex] = [elemKey, elemTuple] // slower: list.push([elemKey, elemTuple])
            listIndex = listIndex + 1

            // create dictionary
            var index = list.length-1
            var tmp = dict
            // var indexes = [elemKey.$y, elemKey.$M, elemKey.$D, elemKey.$H, elemKey.$m] // dayjs
            var indexes = [date.year, date.month, date.day, date.hour, date.minute] // native date

            for (let i=0; i<indexes.length; i++) {
                const elem = indexes[i]
                if (!tmp[elem]) {
                    tmp[elem] = {}
                }
                tmp = tmp[elem]
            }

            tmp[indexes[indexes.length-1]] = index
        }
    }

    if (DEBUG) {
        console.timeEnd("[Utilities] generateTuples")
    }

    return [list, dict]
}

// parses YYYY-MM-DDTHH:mm:ss.000Z to { year: YYYY, month: MM, day: DD, hour: HH, minute: mm }
function databaseDateToObject(date) {
    return {
        year: date.substring(0,4),
        month: Number(date.substring(5,7))-1, // necessary for generating dictionary, e.g. 01 -> 1, 02 -> 2, 03 -> 3
        day: Number(date.substring(8,10)),
        hour: Number(date.substring(11,13)),
        minute: Number(date.substring(14,16))
    }
}

// parses YYYY-MM-DDTHH:mm:ss.000Z to YY-MM-DD_HH-mm.jpg
export function databaseDateToFilename(date) {
    return `${date?.substring(2,10)}_${date?.substring(11,16).replace(/:/, "-")}.jpg`
}

// parses YY-MM-DD_HH-mm.jpg to YYYY-MM-DDTHH:MM (inverse of databaseDateToFilename)
export function filenameToDatabaseDate(filename) {
    const match = filename.match(/^(\d{2})-(\d{2})-(\d{2})_(\d{2})-(\d{2})/)
    if (!match) return null
    return `20${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}`
}

// parses YYYY-MM-DDTHH:mm:ss.000+01:00 to YYYY-MM-DDTHH:mm:ss.000Z
export function databaseDateToUtc(date) {
    return `${date?.substring(0,19)}.000Z`
}

// parses various formats to:
// - YYYY-MM-DD (unchanged, to avoid timezone shifting on day-only values)
// - YYYY-MM-DDTHH:mm:ss.sssZ (canonical ISO)
export function timestampToIso(value) {
    if (!value) return null

    // keep day-only strings stable (no TZ shift)
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value
    }

    // already string (ISO or similar) -> canonicalize if parseable
    if (typeof value === 'string') {
        const ms = Date.parse(value)
        return Number.isFinite(ms) ? new Date(ms).toISOString() : value
    }

    // dayjs-like
    if (typeof value?.toISOString === 'function') {
        const raw = value.toISOString()
        const ms = Date.parse(raw)
        return Number.isFinite(ms) ? new Date(ms).toISOString() : raw
    }

    // Date
    if (value instanceof Date) {
        const ms = value.getTime()
        return Number.isFinite(ms) ? new Date(ms).toISOString() : null
    }

    // epoch ms
    if (typeof value === 'number') {
        const d = new Date(value)
        return Number.isFinite(d.getTime()) ? d.toISOString() : null
    }

    return null
}

export function isSameTimestamp(a, b) {
    const aIso = timestampToIso(a)
    const bIso = timestampToIso(b)
    const ams = Date.parse(aIso)
    const bms = Date.parse(bIso)
    return Number.isFinite(ams) && Number.isFinite(bms) && ams === bms
}

// precompute: YYYY-MM-DD -> last event timestamp of that day (expects ASC-sorted timestamps)
export function buildLastEventByDay(eventTimestamps) {
    const map = {}
    if (!Array.isArray(eventTimestamps)) return map

    for (const elem of eventTimestamps) {
        if (typeof elem !== 'string' || elem.length < 10) continue
        const day = elem.slice(0, 10)
        map[day] = elem  // because eventTimestamps is ASC, last write wins = last event of day
    }

    return map
}

export const DAY_REGEX = /^\d{4}-\d{2}-\d{2}$/

// finds best matching available timestamp (exact -> same day -> nearest)
// eventTimestamps: ASC sorted ISO strings
export function normalizeToAvailableTimestamp(requestedTimestamp, eventTimestamps, lastEventByDay) {
    if (!requestedTimestamp) return null
    if (!Array.isArray(eventTimestamps) || eventTimestamps.length === 0) return null

    // snap to last event of that day if day-string
    if (typeof requestedTimestamp === 'string' && DAY_REGEX.test(requestedTimestamp)) {
        return lastEventByDay?.[requestedTimestamp] ?? null
    }

    const requestedIso = timestampToIso(requestedTimestamp)
    if (!requestedIso) return null

    if (eventTimestamps.includes(requestedIso)) return requestedIso

    const requestedDay = requestedIso.slice(0, 10)
    const sameDayLast = lastEventByDay?.[requestedDay]
    if (sameDayLast) return sameDayLast

    const target = Date.parse(requestedIso)
    if (!Number.isFinite(target)) return eventTimestamps[eventTimestamps.length - 1]

    let best = eventTimestamps[0]
    let bestDiff = Math.abs(Date.parse(best) - target)

    for (let i = 1; i < eventTimestamps.length; i++) {
        const cur = eventTimestamps[i]
        const diff = Math.abs(Date.parse(cur) - target)
        if (diff < bestDiff) {
            best = cur
            bestDiff = diff
        }
    }

    return best
}

// finds smallest date in given array
function findMin(array) {
    let min = array[0].datum
  
    for (let i = 1, len=array.length; i < len; i++) {
        let v = array[i].datum;
        min = (v < min) ? v : min;
    }
  
    return min;
}

// https://www.robinwieruch.de/react-custom-hook-check-if-overflow/
export const useIsOverflow = (ref, callback) => {
    const [isOverflow, setIsOverflow] = useState(undefined)
  
    useLayoutEffect(() => {
        const { current } = ref;
    
        const trigger = () => {
            const hasOverflow = current.scrollHeight > current.clientHeight
            setIsOverflow(hasOverflow)
            if (callback) {
                callback(hasOverflow)
            }
        }
    
        if (current) {
            trigger()
        }
    }, [callback, ref])
  
    return isOverflow
}

export async function sha256(message) {
    const msgBuffer = new TextEncoder().encode(message); // encode as UTF-8
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgBuffer); // hash the message
    const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert ArrayBuffer to Array
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string

    return hashHex;
}

// parses PT<HH>H<MM>M<SS>S to HH:mm
export function databaseTimeToString(time) {
    if (!time) {
        return ""
    }

    const regex = time.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/)

    if (!regex) {
        return ""
    }

    const hours = parseInt(regex[1]) || 0
    const minutes = parseInt(regex[2]) || 0
    const formattedHours = hours.toString().padStart(2, '0')
    const formattedMinutes = minutes.toString().padStart(2, '0')
  
    return `${formattedHours}:${formattedMinutes}`;
}

// parses string to JSON, optional: replacs single quotes with double quotes, replace None with null, replace line breaks with space
export function stringToJson(string, replaceSingleQuotes = true, replaceNoneByNull = true, replaceLineBreak = true) {
    try {
        let result = string

        if (replaceSingleQuotes) {
            result = string.replace(/'/g, '"')
        }

        if (replaceNoneByNull) {
            result = result.replace(/None/g, 'null')
        }

        if (replaceLineBreak) {
            result = result.replace(/(\r\n|\n|\r)/gm, " ")
        }

        return JSON.parse(result)
        
    } catch (error) {
        console.error(error)
        return null
    }
}

export const useStatistics = (component) => {

    const [isStatisticsLoaded, setIsStatisticsLoaded] = useState(false)
    
    const handleLoad = () => {
        setIsStatisticsLoaded(true)
    }

    const handleError = (error) => {
        console.error("[etracker] Failed to initialize:", error)
    }

    const getArea = (component) => {
        switch (component) {
            case 0:
                return "Home";
            case 1:
                return "Live";
            case 2:
                return "Archive";
            case 3:
                return "Timelapse";
            default:
                return "";
        }
    }

    useEffect(() => {
        if (isDevelopment) {
            DEBUG && console.log("[etracker] Development mode active - skipping initialization")
            return
        }

        const existingScript = document.getElementById('_etLoader')
        
        if (existingScript) {
            DEBUG && console.log("[etracker] Script already loaded - skipping initialization")
            setIsStatisticsLoaded(true)
            return
        }

        const script = document.createElement('script');
        const params = document.createElement('script');
        let areaTextNode = document.createTextNode(`var et_pagename = "${CLIENT_ID}";`);

        if (component) {
            const area = getArea(component)
            areaTextNode = document.createTextNode(`var et_pagename = "${CLIENT_ID}"; var et_areas = "${area}";`);
        }

        script.setAttribute("id", "_etLoader");
        script.setAttribute("type", "text/javascript");
        script.setAttribute("charset", "UTF-8");
        script.setAttribute("data-block-cookies", "true");
        script.setAttribute("data-respect-dnt", "true");
        script.setAttribute("data-secure-code", "g9g1km");
        script.setAttribute("src", "//code.etracker.com/code/e.js");
        script.setAttribute("async", "");

        params.setAttribute("id", "_etParams");
        params.setAttribute("type", "text/javascript");
        params.appendChild(areaTextNode);

        document.head.appendChild(params);
        document.head.appendChild(script);

        script.addEventListener('load', handleLoad);
        script.addEventListener('error', handleError);

        return () => {
            script.removeEventListener('load', handleLoad);
            script.removeEventListener('error', handleError);
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isDevelopment) return

        const existingParams = document.getElementById('_etParams')
        
        if (existingParams && component !== undefined) {
            const area = getArea(component)
            existingParams.textContent = `var et_pagename = "${CLIENT_ID}"; var et_areas = "${area}";`
        }
    }, [component])

    return isStatisticsLoaded
}

export function normalizeWeatherCode(code) {
    return parseInt(code?.toString().slice(-2), 10) // 101 -> 1, ..., 116 -> 16
}

export async function checkFile(url) {

    let result = { 
        exists: false,
        contentType: null,
        lastModified: null
    }

    try {
        const response = await fetch(url, { method: 'HEAD' }) // queries only headers, not the entire file!

        // check whether the status code is in the range 200-299 (= successful response)
        if (response.ok) {
            let contentType = response.headers.get('Content-Type')

            if (contentType.includes("image")) {
                contentType = "image"
            } else if (contentType.includes("video")) {
                contentType = "video"
            } else if (contentType.includes("audio")) {
                contentType = "audio"
            } else if (contentType.includes("text")) {
                contentType = "text"
            } else if (contentType.includes("pdf")) {
                contentType = "pdf"
            } else if (contentType.includes("zip")) {
                contentType = "zip"
            } else {
                contentType = "unknown"
            }

            result = {
                exists: true, // returns that file exists
                contentType, // returns the type of file
                lastModified: response.headers.get('Last-Modified') // returns the date of last modification date
            }
        }
    } catch (error) {
        console.error('[Utilities] Error checking file:', error);
    }

    DEBUG && console.log(`[Utilities] File at ${url} exists: ${result.exists}, Content-Type: ${result.contentType}, Last-Modified: ${result.lastModified}`)

    return result
}

const isDevelopment = !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
const isTestserver = window.location.hostname.includes("test") || API.includes("test")

// see https://help.etracker.com/article/event-tracking/
export function trackEvent(objectName, category) {
    if (!isDevelopment && !isTestserver && _etracker && typeof(_etracker) === "object") { // eslint-disable-line no-undef
        DEBUG && console.log("[Utilities] trackEvent", objectName, category)
        _etracker.sendEvent(new et_UserDefinedEvent(objectName, category)) // eslint-disable-line no-undef
    }
}

// see https://help.etracker.com/article/formular-tracking/
export function trackForm(formName) {
    if (!isDevelopment && !isTestserver && etForm && typeof(etForm) === "object") { // eslint-disable-line no-undef
        DEBUG && console.log("[Utilities] trackForm", formName)
        etForm.sendEvent('formView', formName); // eslint-disable-line no-undef
    }
}

export const isMobileApp = typeof window.ReactNativeWebView !== "undefined" // static check if frontend is accessed from mobile app

export function sendDataToMobileApp(data) {
    const dataAsString = JSON.stringify(data)
    DEBUG && console.log(`[Utilities] Sending data to mobile app: ${dataAsString}`)
    window.ReactNativeWebView?.postMessage(dataAsString)
}

export const isIframe = window.self !== window.top // static check if frontend is accessed from iframe

export function sendDataToIframe(data) {
    const parentOrigin = window.location.ancestorOrigins?.[0] || document.referrer || "*"
    DEBUG && console.log(`[Utilities] Sending data to iframe with origin ${parentOrigin}:`, data)
    window.parent?.postMessage(JSON.stringify(data), parentOrigin)
}

export function fetchFile(sourceUrl, targetFilename, setLoading = false) {

    const { isMobileApp: isMobileAppViaManager } = getManagerSearchParams()

    const msg = {
        type: "download",
        baseUrl: window.location.origin,
        sourceUrl,
        targetFilename
    }

    setLoading && setLoading(true)

    if (isMobileApp) {
        DEBUG && console.log(`[Utilities] Sending data to mobile app: ${JSON.stringify(msg)}`)
        sendDataToMobileApp(msg)
        setLoading && setLoading(false)
    } else if (isMobileAppViaManager) {
        DEBUG && console.log(`[Utilities] Sending data to iframe (manager): ${JSON.stringify(msg)}`)
        sendDataToIframe(msg)
        setLoading && setLoading(false)
    } else {
        DEBUG && console.log(`[Utilities] Fetching file from url: ${sourceUrl}`)
        fetch(sourceUrl)
        .then(res => {
            DEBUG && console.log(`[Utilities] Fetching file status: ${res.status} ${res.statusText ? `(${res.statusText})` : ""}`)
            if (res.ok) {
                return res.blob()
            } else {
                throw new Error(res.status)
            }
        })  
        .then(blob => {
            DEBUG && console.log(`[Utilities] Fetched ${(blob.size / (1024 * 1024)).toFixed(2)} MB - saving as: ${targetFilename}`)
            saveFile(blob, targetFilename)
        })
        .catch(error => console.error("[Utilities] Error fetching file:", error))
        .finally(() => setLoading && setLoading(false))
    }
}

// checks if object e.g. dictionary is empty, with less complexity (O(1)) than Object.keys(obj).length (O(N))
export function isEmpty(obj) {
    for (const prop in obj) {
        if (Object.hasOwn(obj, prop)) {
            return false;
        }
    }
  
    return true;
}

export const getManagerSearchParams = () => {
    const searchParams = new URLSearchParams(window.location.search)

    DEBUG && searchParams.toString() && console.log("[Utilities] Search params:", searchParams.toString())

    return {
        isSingleProject: searchParams.get("single") === "true",
        isMobileApp: searchParams.get("app") === "true",
        isManager: searchParams.get("from") === "manager",
        isWeatherAlarm: searchParams.get("alarm") === "true"
    }
}

export const useNavigateWithQueryParams = () => {
    const { search } = useLocation()
    const navigate = useNavigate()

    return (path, options = {}) => {
        const pathWithQueryParams = `${path}${search}`
        const to = typeof path === 'string' ? pathWithQueryParams : path
        DEBUG && console.log(`[Utilities] Navigate to: ${pathWithQueryParams} (options: ${JSON.stringify(options)})`)
        navigate(to, options)
    }
}

export const ConditionalWrapper = ({ condition, wrapper, children }) => {
    return condition ? wrapper(children) : children
}

// EXAMPLE USAGE:
// <StyledBadge
//     badgeContent="BETA" 
//     loading={loading ? 1 : 0}
// >
//     <LibraryBooks sx={{ mr: 1 }} />
//     {t('events.construction_diary.construction_diary_short')}
// </StyledBadge>
export const StyledBadge = styled(Badge, {
    shouldForwardProp: (prop) => !['loading', 'size', 'invertColors', 'badgeOffset'].includes(prop)
})(({ theme, loading = false, size = "medium", invertColors = true, badgeOffset = 0 }) => ({
    ...(badgeOffset && { paddingRight: badgeOffset }),
    '& .MuiBadge-badge': {
        fontSize: size === "medium" ? "0.75rem" : "0.65rem",
        fontWeight: size === "medium" ? 500 : 400,
        padding: size === "medium" ? "0 6px" : "0 4px",
        height: size === "medium" ? 20 : 18,
        border: `1px solid ${invertColors ? "dimgrey" : theme.palette.background.paper}`,
        backgroundColor: loading ? theme.palette.action.disabledBackground : "#FF6600",
        color: loading && invertColors ? "dimgrey" : theme.palette.secondary.main
    }
}))

export const StyledSlider = styled(Slider)({
    color: "#0096fb",
    '& .MuiSlider-markLabel[data-index="0"]': {
        display: "none"
    },
    '& .MuiSlider-markLabel[data-index="1"]': {
        display: "none"
    }
})

export const CloseIconButton = ({ onClick }) => (
    <IconButton
        onClick={onClick}
        sx={(theme) => ({
            position: 'absolute',
            right: 0,
            top: 0,
            color: theme.palette.grey[500],
            p: 0.5
        })}
    >
        <Close sx={{ fontSize: "1.15rem" }} />
    </IconButton>
)

export const useMedia = () => {
    const { i18n } = useTranslation()

    const locale = i18n.language.slice(0, 2).toLowerCase();

    const logos = {
        btv: {
            de: { dark: LogoBtvDarkDe, light: LogoBtvLightDe },
            nl: { dark: LogoBtvDarkNl, light: LogoBtvLightNl },
        },
        netco: {
            de: LogoNetco
        }
    };

    const links = {
        imprint: {
            de: "https://www.netco.de/impressum/",
            nl: "https://bouwtvplus.nl/colofon/",
        },
        privacy: {
            de: "https://www.netco.de/datenschutz/",
            nl: "https://bouwtvplus.nl/privacybeleid/",
        },
        btv: {
            de: "https://www.netco.de/baustellen-webcam/",
            nl: "https://bouwtvplus.nl/",
        },
        netco: {
            de: "https://www.netco.de/",
            nl: "https://www.netco.de/",
        }
    };

    return {
        logos: Object.fromEntries(Object.entries(logos).map(([key, values]) => [key, values[locale] || values.de])),
        links: Object.fromEntries(Object.entries(links).map(([key, values]) => [key, values[locale] || values.de]))
    }
}

export const useNotificationPreference = ({ type: notificationType }) => {

    const { KEYCLOAK: { CLIENT_ID: clientId }, DEBUG } = window.conf
    const { subscriptionId } = useSelector(getData)
    const { getUserProfile, isLoggedIn, isUsersPortal } = useKeycloak()
    
    const [setNotificationPreference, { isLoading: isMutating, error: mutationError }] = useSetNotificationPreferenceMutation()
    const [enabled, setEnabled] = useState(false)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const isAuthorized = isLoggedIn() && isUsersPortal()
    const pendingDesired = useRef(null) // either null or boolean
    const attributeKey = useMemo(() => {
        if (!subscriptionId || !clientId || !notificationType) {
            DEBUG && console.warn(`[Utilities] Something missing - Subscription ID: ${subscriptionId}, Client ID: ${clientId}, Notification type: ${notificationType}`)
            return null
        }
        const key = `${subscriptionId}|${clientId}|${notificationType}`
        DEBUG && console.info(`[Utilities] Using attribute key: ${key}`)
        return key
    }, [subscriptionId, clientId, notificationType, DEBUG]) // eslint-disable-line react-hooks/exhaustive-deps

    const getAttributes = useCallback(async() => {
        if (!attributeKey || !isAuthorized) return
        setLoading(true)
        setError(null)

        try {
            const profile = await getUserProfile()
            const attributes = Array.isArray(profile?.attributes?.notif) ? profile.attributes.notif : []
            const attributeEnabled = attributes.includes(attributeKey)

            if (pendingDesired.current === null) {
                setEnabled(attributeEnabled)
            } else if (pendingDesired.current === attributeEnabled) {
                pendingDesired.current = null
                setEnabled(attributeEnabled)
            }

            DEBUG && console.info(`[Utilities] User has ${notificationType} notifications ${attributeEnabled ? "enabled" : "disabled"}, see attributes: [${attributes}]`)
        } catch (error) {
            setError(error)
            console.error('[Utilities] Getting user attributes failed', error)
        } finally {
            setLoading(false)
        }
    }, [attributeKey, getUserProfile]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        pendingDesired.current = null
        getAttributes()
    }, [attributeKey]) // eslint-disable-line react-hooks/exhaustive-deps

    const setAttribute = useCallback(async(desired) => {
        if (!attributeKey || !isAuthorized) return
        pendingDesired.current = desired
        setEnabled(desired) // optimistic update

        try {
            const request = {
                subscriptionId,
                clientId,
                notificationType,
                enable: desired
            }
            DEBUG && console.log(`[Utilities] Set users notification preference request: ${JSON.stringify(request)}`)
            const response = await setNotificationPreference(request).unwrap()
            DEBUG && console.log(`[Utilities] Set users notification preference response: ${JSON.stringify(response)}`)
            await getAttributes()
        } catch (error) {
            pendingDesired.current = null
            setError(error)
            console.error(`[Utilities] Set user attribute failed: ${error}`)
            await getAttributes()
        }
    }, [attributeKey, clientId, getAttributes, notificationType, subscriptionId, setNotificationPreference, isAuthorized, DEBUG])

    const toggleAttribute = useCallback(() => setAttribute(!enabled), [enabled, setAttribute])

    return {
        enabled,
        toggle: toggleAttribute,
        loading: loading || isMutating,
        error: error || mutationError
    }
}

const DETECTION_TYPE_COLORS = {
    'person': '#FF6B6B', 'car': '#4ECDC4', 'truck': '#45B7D1',
    'excavator': '#FFA07A', 'wheelLoader': '#98D8C8', 'mobileCrane': '#F7DC6F',
    'crane': '#F8C471', 'bulldozer': '#BB8FCE', 'concretePump': '#85C1E9',
    'roadRoller': '#82E0AA', 'smallUtility': '#F1948A', 'tractor': '#AED6F1',
    'tractorTrailer': '#D7BDE2', 'basketLift': '#A3E4D7',
    'asphaltRoller': '#FAD7A0', 'drillType': '#A9CCE3',
    'asphaltingMachine': '#D5F5E3', 'classicForklift': '#FADBD8',
    'levelingVehicle': '#E8DAEF', 'carTrailer': '#D4EFDF',
    'truck*subType_concreteMixer': '#F9E79F', 'car*subType_flatbedTruck': '#ABEBC6',
    'car*subType_transporterStandard': '#D6EAF8', 'basketLift*subType_standard': '#F5CBA7',
    'basketLift*subType_vertical': '#D2B4DE', 'mobileCrane*subType_retractable': '#A9DFBF',
}

const DETECTION_FALLBACK_COLORS = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F',
    '#BB8FCE', '#F8C471', '#85C1E9', '#82E0AA', '#F1948A', '#AED6F1'
]

export function getDetectionColor(type) {
    if (DETECTION_TYPE_COLORS[type]) return DETECTION_TYPE_COLORS[type]
    let h = 0
    for (let i = 0; i < type.length; i++) h = ((h << 5) - h) + type.charCodeAt(i)
    return DETECTION_FALLBACK_COLORS[Math.abs(h) % DETECTION_FALLBACK_COLORS.length]
}

export function hexToRgb(hex) {
    return {
        r: parseInt(hex.slice(1, 3), 16),
        g: parseInt(hex.slice(3, 5), 16),
        b: parseInt(hex.slice(5, 7), 16)
    }
}

export function translateDetectionType(key, t) {
    if (key.includes('*')) {
        const sub = t(`pages.access.vehicle.subtypes.${key}`, '')
        if (sub) return sub
    }
    const main = t(`pages.access.vehicle.types.${key}`, '')
    return main || key
}

let _dashboardCache = { summaryData: null, detailsData: null, weatherHourly: null, weatherDaily: null, loaded: false }

async function fetchCsv(url) {
    const papaparse = (await import('papaparse')).default
    const res = await fetch(url)
    if (!res.ok) throw new Error(`[Utilities] Error loading CSV: ${res.status}`)
    const text = await res.text()
    const { data } = papaparse.parse(text, { header: true, skipEmptyLines: true, dynamicTyping: true })
    return data
}

// convert CSV float columns (0.0) to integers for detection counts, parse spatial strings for details
function postProcessSummary(rows) {
    const skip = new Set(['image_filename', 'timestamp', 'archive_picture_id'])
    return rows.map(elem => {
        const out = {}
        for (const [key, value] of Object.entries(elem)) {
            out[key] = skip.has(key) ? value : (typeof value === 'number' ? Math.round(value) : (value || 0))
        }
        return out
    })
}

function postProcessDetails(rows) {
    return rows.map(elem => ({
        ...elem,
        confidence: typeof elem.confidence === 'number' ? elem.confidence : parseFloat(elem.confidence) || 0,
        bbox: typeof elem.bbox === 'string' ? tryParseJson(elem.bbox) : (elem.bbox || []),
        coords: typeof elem.coords === 'string' ? tryParseJson(elem.coords) : (elem.coords || []),
        area: typeof elem.area === 'number' ? elem.area : parseInt(elem.area) || 0,
    }))
}

function tryParseJson(str) {
    if (!str) return []
    try { return JSON.parse(str) } catch { return [] }
}

export async function loadDashboardData(baseUrl, onProgress) {

    const url = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/'
    const files = [
        ['image_detections_summary.csv', data => { _dashboardCache.summaryData = postProcessSummary(data) }],
        ['image_detections_details.csv', data => { _dashboardCache.detailsData = postProcessDetails(data) }],
        ['weather_hourly.csv', data => { _dashboardCache.weatherHourly = data }],
        ['weather_daily.csv', data => { _dashboardCache.weatherDaily = data }],
    ]
    let loaded = 0

    for (const [file, setter] of files) {
        try {
            setter(await fetchCsv(url + file))
        } catch (error) {
            console.error('[Utilities] Error loading dashboard data', file, error)
        }
        loaded++
        onProgress?.(loaded, files.length, file)
    }

    _dashboardCache.loaded = true
    _dashboardDateRange = null // reset cached date range for fresh computation
}

export function isDashboardDataLoaded() { return _dashboardCache.loaded }

export function getDashboardRawData() { return _dashboardCache }

export function dashboardFmtDate(date) { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` }

export function dashboardParseDate(dateString) {
    dateString = dateString.trim()
    let match = dateString.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/)
    if (match) {
        let y = parseInt(match[3]); if (y < 100) y += 2000;
        return new Date(y, parseInt(match[2]) - 1, parseInt(match[1]))
    }
    match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (match) return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]))
    throw new Error('[Dashboard] Cannot parse date: ' + dateString)
}

let _dashboardDateRange = null

function computeDashboardDateRange() {
    if (_dashboardDateRange) return _dashboardDateRange
    if (!_dashboardCache.summaryData?.length) return null
    let min = Infinity, max = -Infinity
    for (const elem of _dashboardCache.summaryData) {
        const ts = new Date(elem.timestamp).getTime()
        if (ts < min) min = ts
        if (ts > max) max = ts
    }
    _dashboardDateRange = { min: dashboardFmtDate(new Date(min)), max: dashboardFmtDate(new Date(max)) }
    return _dashboardDateRange
}

export function getDashboardMinDate() { return computeDashboardDateRange()?.min || null }

export function getDashboardMaxDate() { return computeDashboardDateRange()?.max || null }